// Updated BookingV2Dashboard.js with Service Management integration

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Check, Eye, Settings, Users, Calendar, BarChart3, Globe, Sparkles, ArrowUp, ArrowDown, ExternalLink, Briefcase } from 'lucide-react';
import '../../styles/BookingV2Dashboard.css';
import CalendarManagement from './CalendarManagement';
import SimpleBookingConfig from './SimpleBookingConfig';
import FormBuilderV2 from './FormBuilderV2';
import ServiceManagement from './ServiceManagement'; 

const BookingV2Dashboard = () => {
  const [activeView, setActiveView] = useState('overview');
  const [config, setConfig] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [embedCode, setEmbedCode] = useState('');
  const [iframeCode, setIframeCode] = useState('');
  const [testUrl, setTestUrl] = useState('');
  const [copied, setCopied] = useState({ embed: false, iframe: false });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [saving, setSaving] = useState(false);
  const [calendarData, setCalendarData] = useState(null);
  const [services, setServices] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);

  const [simpleConfig, setSimpleConfig] = useState({
    mode: 'simple', // simple, capacity, services
    businessHours: {
      weekdayOpen: '09:00',
      weekdayClose: '17:00',
      saturdayOpen: '09:00',
      saturdayClose: '15:00',
      sundayOpen: false
    },
    capacity: {
      maxConcurrent: 3
    }
  });

  const RESERVED_FIELD_NAMES = {
  SERVICE_SELECTION: 'service_selection',
  CUSTOMER_NAME: 'customer_name', 
  CUSTOMER_EMAIL: 'customer_email',
  CUSTOMER_PHONE: 'customer_phone'
};

  // Updated views array to include Service Management
  const views = [
    { id: 'overview', label: 'Overview', icon: BarChart3, color: 'bg-blue-500' },
    { id: 'setup', label: 'Quick Setup', icon: Settings, color: 'bg-orange-500' },
    { id: 'services', label: 'Services', icon: Briefcase, color: 'bg-indigo-500' }, 
    { id: 'calendar', label: 'Manage Bookings', icon: Calendar, color: 'bg-green-500' },
    { id: 'configure', label: 'Form Builder', icon: Settings, color: 'bg-purple-500' },
    { id: 'embed', label: 'Embed Widget', icon: Globe, color: 'bg-pink-500' }
  ];

  const fieldTypes = [
    { value: 'text', label: 'Text Input', icon: '📝' },
    { value: 'email', label: 'Email', icon: '📧' },
    { value: 'tel', label: 'Phone', icon: '📞' },
    { value: 'select', label: 'Dropdown', icon: '📋' },
    { value: 'textarea', label: 'Text Area', icon: '📄' },
    { value: 'url', label: 'Website', icon: '🌐' }
  ];

  const beautyServices = [
    { value: 'facial', label: 'Facial Treatment' },
    { value: 'massage', label: 'Massage Therapy' },
    { value: 'manicure', label: 'Manicure' },
    { value: 'pedicure', label: 'Pedicure' },
    { value: 'eyebrows', label: 'Eyebrow Treatment' },
    { value: 'waxing', label: 'Waxing Services' },
    { value: 'lashes', label: 'Eyelash Extensions' },
    { value: 'skincare', label: 'Skincare Consultation' },
    { value: 'hair', label: 'Hair Services' }
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (activeView === 'bookings') loadBookings();
    if (activeView === 'overview') loadAnalytics();
    if (activeView === 'calendar') loadCalendarData();
    if (activeView === 'setup') loadSimpleConfig(); 
    if (activeView === 'configure') loadServices();
  }, [activeView, filter]);


useEffect(() => {
  if (availableServices.length > 0) {
    updateServiceDropdown();
  }
}, [availableServices]);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/booking-v2/vendor/config', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Load vendor email settings
        const vendorResponse = await fetch('/api/vendors/profile', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (vendorResponse.ok) {
          const vendorData = await vendorResponse.json();
          data.template.booking_v2_notification_email = vendorData.booking_v2_notification_email;
          data.template.booking_v2_cc_emails = vendorData.booking_v2_cc_emails || [];
        }
        
        setConfig(data.template);
        await loadEmbedCode();
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSimpleConfig = async () => {
    try {
      const [hoursResponse, vendorResponse] = await Promise.all([
        fetch('/api/booking-v2/vendor/business-hours', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/booking-v2/vendor/profile', { // Changed to V2 route
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (hoursResponse.ok && vendorResponse.ok) {
        const hours = await hoursResponse.json();
        const vendor = await vendorResponse.json();
        
        // Convert business hours to simple config format
        const weekdayHours = hours.businessHours?.find(h => h.day_of_week === 1);
        const saturdayHours = hours.businessHours?.find(h => h.day_of_week === 6);
        const sundayHours = hours.businessHours?.find(h => h.day_of_week === 0);
        
        setSimpleConfig(prev => ({
          ...prev,
          businessHours: {
            weekdayOpen: weekdayHours?.open_time || '09:00',
            weekdayClose: weekdayHours?.close_time || '17:00',
            saturdayOpen: saturdayHours?.open_time || '09:00',
            saturdayClose: saturdayHours?.close_time || '15:00',
            sundayOpen: sundayHours?.is_open || false
          },
          capacity: {
            maxConcurrent: vendor.default_concurrent_capacity || 3
          },
          mode: vendor.default_concurrent_capacity >= 999 ? 'simple' : 'capacity'
        }));
      }
    } catch (error) {
      console.error('Error loading simple config:', error);
    }
  };

const loadServices = async () => {
  try {
    const response = await fetch('/api/booking-v2/vendor/services', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      const data = await response.json();
      setAvailableServices(data.services || []);
      
      // Auto-update service dropdown if it exists
      updateServiceDropdown();
    }
  } catch (error) {
    console.error('Error loading services:', error);
  }
};


const updateServiceDropdown = () => {
  if (!config || !hasServiceField()) return;
  
  // Only include active services in form builder
  const activeServices = availableServices.filter(service => service.is_active);
  
  const serviceOptions = activeServices.map(service => ({
    value: service.id.toString(),
    label: `${service.service_name}${service.price ? ` - R${service.price}` : ''}${service.duration_minutes ? ` (${service.duration_minutes}min)` : ''}`
  }));

  // Update the service field in form_fields
  const updatedFields = config.form_fields.map(field => {
    if (field.name === RESERVED_FIELD_NAMES.SERVICE_SELECTION && field.reserved_field) {
      return { ...field, options: serviceOptions };
    }
    return field;
  });

  setConfig(prev => ({
    ...prev,
    form_fields: updatedFields
  }));
};

const hasServiceField = () => {
  return config.form_fields?.some(field => 
    field.name === RESERVED_FIELD_NAMES.SERVICE_SELECTION && field.reserved_field
  );
};

// Function to add service dropdown from catalog
const addServiceDropdown = () => {
  // Filter for active services only in form builder
  const activeServices = availableServices.filter(service => service.is_active);
  
  if (activeServices.length === 0) {
    alert('Please add active services to your catalog first before adding a service selection field.');
    return;
  }

  const serviceOptions = activeServices.map(service => ({
    value: service.id.toString(),
    label: `${service.service_name}${service.price ? ` - R${service.price}` : ''}${service.duration_minutes ? ` (${service.duration_minutes}min)` : ''}`
  }));

  const serviceField = {
    name: RESERVED_FIELD_NAMES.SERVICE_SELECTION, // Cannot be changed
    label: 'Select Service', // Can be changed by user
    type: 'select',
    required: true,
    default_field: false,
    reserved_field: true, // Mark as reserved
    order: config.form_fields.length + 1,
    options: serviceOptions,
    placeholder: 'Choose a service...'
  };

  setConfig(prev => ({
    ...prev,
    form_fields: [...prev.form_fields, serviceField]
  }));
};


  const loadCalendarData = async () => {
    if (activeView !== 'calendar') return;
    
    try {
      const [calendarResponse, servicesResponse] = await Promise.all([
        fetch('/api/booking-v2/vendor/calendar', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/booking-v2/vendor/services', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (calendarResponse.ok) {
        const calendarData = await calendarResponse.json();
        setCalendarData(calendarData);
      }

      if (servicesResponse.ok) {
        const servicesData = await servicesResponse.json();
        setServices(servicesData.services || []);
      }
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };

  const handleCalendarDataChange = () => {
    loadCalendarData();
    // Also refresh bookings if they're affected
    if (activeView === 'bookings') {
      loadBookings();
    }
  };

  const loadEmbedCode = async () => {
    try {
      const response = await fetch('/api/booking-v2/vendor/embed-code', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmbedCode(data.embedCode);
        setIframeCode(data.iframeCode);
        setTestUrl(data.testUrl);
      }
    } catch (error) {
      console.error('Error loading embed code:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await fetch(`/api/booking-v2/vendor/bookings?filter=${filter}&limit=50`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch('/api/booking-v2/vendor/analytics?period=month', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Save form template configuration
      const templateResponse = await fetch('/api/booking-v2/vendor/config', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formFields: config.form_fields,
          widgetSettings: config.widget_settings,
          templateName: config.template_name
        })
      });

      // Save email notification settings (separate call)
      const notificationResponse = await fetch('/api/booking-v2/vendor/notifications', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notification_email: config.booking_v2_notification_email,
          cc_emails: config.booking_v2_cc_emails ? config.booking_v2_cc_emails.join(', ') : ''
        })
      });

      if (templateResponse.ok && notificationResponse.ok) {
        alert('Configuration saved successfully! 🎉');
        await loadEmbedCode();
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const approveBooking = async (bookingId) => {
    try {
      const response = await fetch(`/api/booking-v2/vendor/bookings/${bookingId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        loadBookings();
        alert('Booking approved successfully! Customer will receive confirmation email.');
      }
    } catch (error) {
      alert('Error approving booking');
    }
  };

  const copyCode = (type) => {
    const code = type === 'embed' ? embedCode : iframeCode;
    navigator.clipboard.writeText(code);
    setCopied(prev => ({ ...prev, [type]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [type]: false })), 2000);
  };

  // Add this function after line 200 (in the configure section)
  const updateFieldOptions = (fieldIndex, newOptions) => {
    const newFields = config.form_fields.map((field, index) => 
      index === fieldIndex 
        ? { ...field, options: newOptions }
        : field
    );
    setConfig(prev => ({ ...prev, form_fields: newFields }));
  };

  const addFieldOption = (fieldIndex) => {
    const field = config.form_fields[fieldIndex];
    const newOption = { value: `option_${Date.now()}`, label: 'New Option' };
    updateFieldOptions(fieldIndex, [...(field.options || []), newOption]);
  };

  const removeFieldOption = (fieldIndex, optionIndex) => {
    const field = config.form_fields[fieldIndex];
    const newOptions = field.options.filter((_, i) => i !== optionIndex);
    updateFieldOptions(fieldIndex, newOptions);
  };

  const updateFieldOption = (fieldIndex, optionIndex, key, value) => {
    const field = config.form_fields[fieldIndex];
    const newOptions = field.options.map((option, i) => 
      i === optionIndex 
        ? { ...option, [key]: value }
        : option
    );
    updateFieldOptions(fieldIndex, newOptions);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
        <span className="ml-3 text-gray-600">Loading Booking V2...</span>
      </div>
    );
  }

  return (
    <div className="booking-v2-dashboard">
      {/* Header */}
      <div className="bv2-header">
        <div className="bv2-header-content">
          <div className="bv2-header-title">
            <Sparkles className="bv2-header-icon" />
            <div>
              <h1>Booking System V2</h1>
              <p>Advanced booking system for beauty & spa businesses</p>
            </div>
          </div>
          <div className="bv2-header-badges">
            <span className="bv2-badge bv2-badge-new">NEW</span>
            <span className="bv2-badge bv2-badge-beauty">✨ Beauty & Spa</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bv2-nav">
        {views.map(view => {
          const IconComponent = view.icon;
          return (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`bv2-nav-item ${activeView === view.id ? 'active' : ''}`}
            >
              <div className={`bv2-nav-icon ${view.color}`}>
                <IconComponent className="w-4 h-4" />
              </div>
              <span>{view.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="bv2-content">
        {/* Overview */}
        {activeView === 'overview' && (
          <div className="bv2-overview">
            <div className="bv2-feature-grid">
              <div className="bv2-feature-card">
                <div className="bv2-feature-icon">🚀</div>
                <h3>No Registration Required</h3>
                <p>Customers book instantly with just name, email, and phone</p>
              </div>
              <div className="bv2-feature-card">
                <div className="bv2-feature-icon">💄</div>
                <h3>Beauty & Spa Optimized</h3>
                <p>Pre-built service types, staff preferences, and client history</p>
              </div>
              <div className="bv2-feature-card">
                <div className="bv2-feature-icon">📱</div>
                <h3>Multi-Step Experience</h3>
                <p>Professional booking flow with step indicators and validation</p>
              </div>
              <div className="bv2-feature-card">
                <div className="bv2-feature-icon">🌐</div>
                <h3>Universal Embed</h3>
                <p>Works on any website and inherits your site's styling</p>
              </div>
            </div>

            {analytics && (
              <div className="bv2-stats-grid">
                <div className="bv2-stat-card">
                  <div className="bv2-stat-number">{analytics.summary?.total_bookings || 0}</div>
                  <div className="bv2-stat-label">Total V2 Bookings</div>
                </div>
                <div className="bv2-stat-card">
                  <div className="bv2-stat-number">{analytics.summary?.anonymous_bookings || 0}</div>
                  <div className="bv2-stat-label">Anonymous Customers</div>
                </div>
                <div className="bv2-stat-card">
                  <div className="bv2-stat-number">{analytics.summary?.unique_customers || 0}</div>
                  <div className="bv2-stat-label">Unique Customers</div>
                </div>
                <div className="bv2-stat-card">
                  <div className="bv2-stat-number">{analytics.summary?.pending_bookings || 0}</div>
                  <div className="bv2-stat-label">Pending Approval</div>
                </div>
              </div>
            )}

            <div className="bv2-quick-actions">
              <button onClick={() => setActiveView('services')} className="bv2-quick-action">
                <Briefcase className="w-5 h-5" />
                Manage Services
              </button>
              <button onClick={() => setActiveView('calendar')} className="bv2-quick-action">
                <Calendar className="w-5 h-5" />
                Open Calendar
              </button>
              <button onClick={() => setActiveView('configure')} className="bv2-quick-action">
                <Settings className="w-5 h-5" />
                Configure Form Fields
              </button>
              <button onClick={() => setActiveView('embed')} className="bv2-quick-action">
                <Globe className="w-5 h-5" />
                Get Embed Code
              </button>
              {testUrl && (
                <a href={testUrl} target="_blank" rel="noopener noreferrer" className="bv2-quick-action">
                  <ExternalLink className="w-5 h-5" />
                  Test Widget
                </a>
              )}
            </div>
          </div>
        )}

        {activeView === 'setup' && <SimpleBookingConfig />}

        {/* Service Management - NEW TAB */}
        {activeView === 'services' && (
          <div className="bv2-services">
            <div className="bv2-section-header">
              <h2>Service Management</h2>
              <div className="bv2-services-info">
                <span className="bv2-info-badge">
                  Configure your services, pricing, duration, and staff assignments
                </span>
              </div>
            </div>
            <ServiceManagement />
          </div>
        )}

    
        {/* Form Configuration — moved into the FormBuilderV2 module */}
        {activeView === 'configure' && <FormBuilderV2 />}

        {activeView === 'calendar' && (
          <div className="bv2-calendar">
            <div className="bv2-section-header">
              <h2>Calendar Management</h2>
              <div className="bv2-calendar-info">
                <span className="bv2-info-badge">
                  Advanced booking calendar with service integration
                </span>
              </div>
            </div>
            
            {calendarData ? (
              <CalendarManagement 
                calendarData={calendarData}
                services={services}
                onDataChange={handleCalendarDataChange}
              />
            ) : (
              <div className="bv2-loading">
                <div className="bv2-spinner"></div>
                <span>Loading calendar...</span>
              </div>
            )}
          </div>
        )}

        {/* Embed Code */}
        {activeView === 'embed' && (
          <div className="bv2-embed">
            <div className="bv2-section">
              <h2>Embed Your V2 Booking Widget</h2>
              
              <div className="bv2-embed-methods">
                <div className="bv2-embed-method">
                  <h3>🚀 JavaScript Widget (Recommended)</h3>
                  <p>Seamless integration that inherits your website's styling</p>
                  <div className="bv2-code-block">
                    <pre><code>{embedCode}</code></pre>
                    <button
                      onClick={() => copyCode('embed')}
                      className={`bv2-copy-btn ${copied.embed ? 'copied' : ''}`}
                    >
                      {copied.embed ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied.embed ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="bv2-embed-method">
                  <h3>🖼️ Iframe Embed (Alternative)</h3>
                  <p>Simple iframe for quick implementation</p>
                  <div className="bv2-code-block">
                    <pre><code>{iframeCode}</code></pre>
                    <button
                      onClick={() => copyCode('iframe')}
                      className={`bv2-copy-btn ${copied.iframe ? 'copied' : ''}`}
                    >
                      {copied.iframe ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied.iframe ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="bv2-test-widget">
                <h3>🧪 Test Your Widget</h3>
                <p>Preview how your widget will look and function</p>
                {testUrl && (
                  <a 
                    href={testUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bv2-button bv2-button-primary"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Test Page
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingV2Dashboard;